// Секция «Настройки»: оформление, уведомления, горячие клавиши, импорт/экспорт.

import { el, clear, bus, fmtVolume } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { settings } from '../core/state.js';
import { btcWidgetState } from '../ui/btc-widget.js';
import { tiger, tigerState, saveTiger, TIGER_EXCHANGES } from '../core/tiger.js';
import {
  THEMES, applyTheme, currentThemeId, candleColors, candlesCustomized,
  setCandleColors, resetCandleColors,
} from '../core/theme.js';
import { toggle, toast, modal } from '../ui/components.js';
import { TOOLS } from '../chart/drawings.js';

const TABS = [
  { id: 'appearance', label: 'Оформление', icon: 'palette' },
  { id: 'notifications', label: 'Уведомления', icon: 'bell' },
  { id: 'hotkeys', label: 'Горячие клавиши', icon: 'rows' },
  { id: 'data', label: 'Импорт / Экспорт', icon: 'share' },
];

/**
 * `<input type="color">` acceptă doar `#rrggbb`. Temele pot da forma scurtă sau
 * `rgb()`, iar un format neînțeles ar face câmpul să arate negru fără niciun motiv.
 */
function toHex(color) {
  const value = String(color).trim();
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value.slice(1).split('').map((c) => c + c).join('')}`.toLowerCase();
  }
  const nums = value.match(/\d+/g);
  if (nums && nums.length >= 3) {
    return `#${nums.slice(0, 3).map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
  }
  return '#000000';
}

/** Ce se poate schimba separat la lumânări. */
const CANDLE_FIELDS = [
  { key: 'up', label: 'Тело роста' },
  { key: 'down', label: 'Тело падения' },
  { key: 'upWick', label: 'Фитиль роста' },
  { key: 'downWick', label: 'Фитиль падения' },
];

export class SettingsSection {
  constructor(app) {
    this.app = app;
    this.tab = 'appearance';
  }

  async mount(host) {
    this.root = el('div', { class: 'settings-section' });
    this.tabsEl = el('div', { class: 'set-tabs' }, TABS.map((tab) => el('button', {
      class: `set-tab${tab.id === this.tab ? ' active' : ''}`,
      onclick: () => {
        this.tab = tab.id;
        [...this.tabsEl.children].forEach((c) => c.classList.toggle('active', c.dataset.id === tab.id));
        this.#render();
      },
      dataset: { id: tab.id },
    }, [icon(tab.icon, 14), el('span', { text: tab.label })])));

    this.bodyEl = el('div', { class: 'set-body' });
    this.root.append(this.tabsEl, this.bodyEl);
    host.append(this.root);
    await this.#render();
  }

  unmount() {
    this.root?.remove();
  }

  async #render() {
    clear(this.bodyEl);
    if (this.tab === 'appearance') this.#renderAppearance();
    else if (this.tab === 'notifications') await this.#renderNotifications();
    else if (this.tab === 'hotkeys') this.#renderHotkeys();
    else this.#renderData();
  }


  // --------------------------------------------------------- Tiger.Trade

  /**
   * Integrarea cu terminalul Tiger.Trade.
   *
   * Terminalul deschide un server WebSocket local (implicit `127.0.0.1:7819`), pornit
   * din `Settings → Local Signal Server`. Numele burselor diferă între versiuni — în
   * documentația lor apar și „BINANCE", și „TigerX Binance" — de aceea fiecare se poate
   * corecta aici, fără să atingem codul.
   */
  #tigerCard() {
    const st = tigerState();
    const statusEl = el('div', { class: 'tiger-status' }, [
      el('span', { class: 'dot' }),
      this.tigerTxt = el('span', { text: 'Не проверено' }),
    ]);

    const pune = ({ status, detail }) => {
      statusEl.className = `tiger-status ${status}`;
      this.tigerTxt.textContent = {
        idle: 'Не подключено',
        connecting: 'Подключаюсь…',
        open: 'Терминал отвечает',
        error: detail || 'Терминал не отвечает',
      }[status] || status;
    };
    this.offTiger?.();
    this.offTiger = bus.on('tiger:status', pune);

    this.offTigerLog?.();
    this.offTigerLog = bus.on('tiger:log', ({ dir, text }) => {
      if (!this.tigerLog) return;
      this.tigerLog.querySelector('.tiger-log-empty')?.remove();
      this.tigerLog.append(el('div', { class: `tiger-log-row ${dir}` }, [
        el('span', { class: 'tiger-log-dir', text: dir === 'out' ? '→' : '←' }),
        el('span', { class: 'tiger-log-txt', text }),
      ]));
      while (this.tigerLog.children.length > 8) this.tigerLog.firstChild.remove();
      this.tigerLog.scrollTop = this.tigerLog.scrollHeight;
    });

    const portEl = el('input', {
      class: 'auth-input mini', type: 'number', value: st.port,
      oninput: (e) => saveTiger({ port: Number(e.target.value) || 7819 }),
    });
    const groupEl = el('input', {
      class: 'auth-input mini', value: st.linkGroup, maxlength: 2,
      oninput: (e) => saveTiger({ linkGroup: e.target.value.trim().toUpperCase() || 'A' }),
    });

    // Două câmpuri per bursă: numele diferă după piață. Binance perpetue e
    // „TigerX Binance", iar spotul „BINANCE" — verificat pe terminal real.
    const numeCurent = (ex, piata) => {
      const v = tigerState().names?.[ex];
      if (typeof v === 'string') return v;
      return v?.[piata] ?? TIGER_EXCHANGES[ex][piata];
    };
    const campNume = (ex, piata) => el('input', {
      class: 'auth-input mini', value: numeCurent(ex, piata),
      placeholder: TIGER_EXCHANGES[ex][piata],
      oninput: (e) => {
        const vechi = tigerState().names?.[ex];
        const baza = typeof vechi === 'object' && vechi ? vechi : { ...TIGER_EXCHANGES[ex] };
        saveTiger({ names: { ...tigerState().names, [ex]: { ...baza, [piata]: e.target.value.trim() } } });
      },
    });

    const nume = el('div', { class: 'tiger-names' }, Object.keys(TIGER_EXCHANGES).map((ex) => el('div', { class: 'tiger-name-row' }, [
      el('span', { class: 'tiger-name-k', text: ex }),
      el('label', { class: 'tiger-name-f' }, [el('span', { text: 'фьюч.' }), campNume(ex, 'futures')]),
      el('label', { class: 'tiger-name-f' }, [el('span', { text: 'спот' }), campNume(ex, 'spot')]),
    ])));

    const corp = el('div', { class: 'set-card-body', style: { display: st.on ? '' : 'none' } }, [
      el('p', { class: 'set-card-sub', text: 'В терминале: Settings → Local Signal Server → Enable Signal Server → Save.' }),
      el('div', { class: 'idea-form-row' }, [
        el('label', { class: 'tiger-name' }, [el('span', { class: 'tiger-name-k', text: 'Порт' }), portEl]),
        el('label', { class: 'tiger-name' }, [el('span', { class: 'tiger-name-k', text: 'Группа' }), groupEl]),
        el('label', { class: 'tiger-name', title: 'На какой бирже открывать монеты из «Формаций»' }, [
          el('span', { class: 'tiger-name-k', text: 'Торгую на' }),
          el('select', {
            class: 'auth-input mini',
            onchange: (e) => saveTiger({ tradeExchange: e.target.value }),
          }, [['', 'где найдено'], ...Object.keys(TIGER_EXCHANGES).map((ex) => [ex, ex])].map(([v, t]) => el('option', {
            value: v, text: t, selected: (st.tradeExchange ?? 'bybit') === v,
          }))),
        ]),
        el('button', { class: 'btn', text: 'Проверить связь', onclick: () => tiger.test() }),
        statusEl,
      ]),
      el('div', { class: 'idea-form-label', text: 'Названия бирж в терминале' }),
      nume,
      el('div', { class: 'idea-form-label', text: 'Диалог с терминалом' }),
      // Ce trimitem și ce răspunde terminalul, la vedere: „соединение есть, но ничего
      // не происходит" nu se poate rezolva fără să vezi replicile.
      this.tigerLog = el('div', { class: 'tiger-log' }, [
        el('div', { class: 'tiger-log-empty', text: 'Пусто. Нажмите «Проверить связь» или значок рядом с монетой.' }),
      ]),
    ]);

    return el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('bolt', 16), el('h3', { text: 'Tiger.Trade' })]),
      toggle(st.on, (v) => {
        saveTiger({ on: v });
        corp.style.display = v ? '' : 'none';
        // Iconițele apar și dispar odată cu comutatorul, fără reîncărcare.
        bus.emit('market:aggregates');
      }, {
        title: 'Открывать монету в терминале',
        subtitle: 'Рядом с каждой монетой появится значок «T» — клик открывает пару '
                + 'в запущенном Tiger.Trade через его локальный сервер.',
      }),
      corp,
    ]);
  }

  // ------------------------------------------------------------- оформление

  #renderAppearance() {
    const themes = el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('palette', 16), el('h3', { text: 'Тема' })]),
      el('p', {
        class: 'set-card-sub',
        text: 'Меняет и интерфейс, и график — сразу, без перезагрузки. '
            + 'Цвета свечей ниже настраиваются отдельно и переживают смену темы.',
      }),
      this.themesEl = el('div', { class: 'set-themes' }),
    ]);

    const candles = el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [
        icon('candles', 16),
        el('h3', { text: 'Цвета свечей' }),
      ]),
      el('p', {
        class: 'set-card-sub',
        text: 'Объём на графике подхватывает цвета тел автоматически.',
      }),
      this.candlesEl = el('div', { class: 'set-candles' }),
    ]);

    const widgets = el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('window', 16), el('h3', { text: 'Виджеты' })]),
      toggle(btcWidgetState().on, (v) => bus.emit('btcwidget:toggle', v), {
        title: 'Плавающее окно BTC',
        subtitle: 'Маленький график биткоина поверх страницы — виден на всех вкладках '
                + 'скринера. Тянется за шапку, размер меняется за правый нижний угол; '
                + 'место, размер и таймфрейм запоминаются.',
      }),
    ]);


    this.bodyEl.append(themes, candles, widgets, this.#tigerCard());
    this.#renderThemeCards();
    this.#renderCandleFields();
  }

  #renderThemeCards() {
    clear(this.themesEl);
    const active = currentThemeId();

    for (const theme of THEMES) {
      const card = el('button', {
        class: `set-theme${theme.id === active ? ' active' : ''}`,
        onclick: () => {
          applyTheme(theme.id);
          this.#renderThemeCards();
          // Culorile implicite s-au schimbat odată cu tema; câmpurile arată acum
          // valorile ei, dacă utilizatorul nu are alegeri proprii.
          this.#renderCandleFields();
          toast(`Тема «${theme.label}»`, 'success');
        },
      }, [
        el('span', { class: 'set-theme-swatch' },
          theme.swatch.map((c) => el('i', { style: { background: c } }))),
        el('span', { class: 'set-theme-name', text: theme.label }),
        el('span', { class: 'set-theme-hint', text: theme.hint }),
      ]);
      this.themesEl.append(card);
    }
  }

  #renderCandleFields() {
    clear(this.candlesEl);
    const colors = candleColors();

    for (const field of CANDLE_FIELDS) {
      const value = colors[field.key];
      const swatch = el('span', { class: 'set-color-dot', style: { background: value } });
      const code = el('span', { class: 'set-color-code', text: value.toUpperCase() });

      const input = el('input', {
        type: 'color',
        class: 'set-color-input',
        value: toHex(value),
        oninput: (e) => {
          const next = e.target.value;
          swatch.style.background = next;
          code.textContent = next.toUpperCase();
          setCandleColors({ [field.key]: next });
          this.#syncResetButton();
        },
      });

      this.candlesEl.append(el('label', { class: 'set-color-row' }, [
        swatch,
        el('span', { class: 'set-color-label', text: field.label }),
        code,
        input,
      ]));
    }

    this.resetBtn = el('button', {
      class: 'btn set-color-reset',
      text: 'Вернуть цвета темы',
      onclick: () => {
        resetCandleColors();
        this.#renderCandleFields();
        toast('Цвета свечей сброшены', 'success');
      },
    });
    this.candlesEl.append(this.resetBtn);
    this.#syncResetButton();
  }

  /** Butonul de revenire n-are ce reseta dacă nimic nu e schimbat. */
  #syncResetButton() {
    if (!this.resetBtn) return;
    this.resetBtn.disabled = !candlesCustomized();
  }

  // ------------------------------------------------------------ уведомления

  async #renderNotifications() {
    let info = {};
    try {
      info = await (await fetch('/api/alerts/settings')).json();
    } catch { /* serverul poate fi oprit */ }

    const prefs = { ...(settings.get('sx_alert_prefs', {}) || {}) };
    const save = (patch) => {
      Object.assign(prefs, patch);
      settings.set('sx_alert_prefs', prefs);
    };

    const card = el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [
        icon('bell', 16),
        el('h3', { text: 'Алерты в Telegram' }),
      ]),
      el('p', {
        class: 'set-card-sub',
        text: 'Уровни, которые вы ставите на графике, проверяются на сервере — '
            + 'поэтому уведомление придёт даже когда вкладка со скринером закрыта.',
      }),

      el('div', { class: 'set-link-row' }, [
        el('span', { class: `set-status ${info.linked ? 'on' : ''}` }),
        el('span', { text: info.linked ? 'Telegram подключён' : 'Telegram не подключён' }),
        info.bot_username
          ? el('a', {
            class: 'btn primary',
            href: `https://t.me/${info.bot_username}`,
            target: '_blank', rel: 'noreferrer',
            text: 'Подключить Telegram',
          })
          : el('span', {
            class: 'set-note',
            text: 'Задайте SS_TELEGRAM_BOT_TOKEN и SS_TELEGRAM_BOT_USERNAME, чтобы включить бота',
          }),
      ]),

      toggle(prefs.notify_telegram !== false, (v) => save({ notify_telegram: v }), {
        title: 'Алерты в Telegram', subtitle: 'Присылать срабатывания уровней в бот',
      }),
      toggle(prefs.notify_in_app !== false, (v) => save({ notify_in_app: v }), {
        title: 'Алерты в уведомления платформы', subtitle: 'Дублировать в колокольчик на сайте',
      }),
      toggle(prefs.sound_enabled !== false, (v) => save({ sound_enabled: v }), {
        title: 'Звук в браузере', subtitle: 'Звуковой сигнал при срабатывании на открытой вкладке',
      }),
    ]);

    const alerts = el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('rows', 16), el('h3', { text: 'Активные уровни' })]),
      this.alertsEl = el('div', { class: 'set-alerts' }),
    ]);

    this.bodyEl.append(card, alerts);
    await this.#renderAlerts();
  }

  async #renderAlerts() {
    clear(this.alertsEl);
    let data = { active: [], fired: [] };
    try {
      data = await (await fetch('/api/alerts')).json();
    } catch { /* ignorat */ }

    if (!data.active?.length && !data.fired?.length) {
      this.alertsEl.append(el('div', { class: 'set-empty', text: 'Уровней пока нет. Поставьте их кнопкой-колокольчиком на графике.' }));
      return;
    }

    for (const alert of data.active || []) {
      this.alertsEl.append(el('div', { class: 'set-alert' }, [
        el('span', { class: 'set-alert-sym', text: alert.symbol }),
        el('span', { class: 'set-alert-price', text: String(alert.price) }),
        el('span', { class: 'set-alert-meta', text: `${alert.exchange} · ${alert.market}` }),
        el('button', {
          class: 'set-alert-del', title: 'Удалить',
          onclick: async () => {
            await fetch(`/api/alerts/${alert.id}/delete`, { method: 'POST' });
            this.#renderAlerts();
          },
        }, [icon('trash', 12)]),
      ]));
    }

    for (const alert of (data.fired || []).slice(0, 20)) {
      this.alertsEl.append(el('div', { class: 'set-alert fired' }, [
        el('span', { class: 'set-alert-sym', text: alert.symbol }),
        el('span', { class: 'set-alert-price', text: String(alert.price) }),
        el('span', { class: 'set-alert-meta', text: 'сработал' }),
      ]));
    }
  }

  // -------------------------------------------------------- горячие клавиши

  #renderHotkeys() {
    const rows = [
      ...TOOLS.map((t) => [t.hotkey, t.label]),
      ['Ctrl+Z', 'Отменить последний рисунок'],
      ['Esc', 'Отменить текущее построение'],
      ['G / Ctrl', 'Магнит к O/H/L/C'],
    ];

    this.bodyEl.append(el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('rows', 16), el('h3', { text: 'Горячие клавиши графика' })]),
      el('div', { class: 'set-hotkeys' }, rows.map(([key, label]) => el('div', { class: 'set-hotkey' }, [
        el('kbd', { text: key }),
        el('span', { text: label }),
      ]))),
    ]));
  }

  // ---------------------------------------------------------- импорт/экспорт

  #renderData() {
    this.bodyEl.append(el('div', { class: 'set-card' }, [
      el('div', { class: 'set-card-head' }, [icon('share', 16), el('h3', { text: 'Импорт / Экспорт' })]),
      el('p', {
        class: 'set-card-sub',
        text: 'Фильтры, индикаторы, закладки и рисунки на графиках — одним файлом.',
      }),
      el('div', { class: 'set-actions' }, [
        el('button', { class: 'btn primary', text: 'Экспортировать настройки', onclick: () => this.#export() }),
        el('label', { class: 'btn' }, [
          el('span', { text: 'Импортировать' }),
          el('input', {
            type: 'file', accept: 'application/json',
            style: { display: 'none' },
            onchange: (e) => this.#import(e.target.files[0]),
          }),
        ]),
        el('button', {
          class: 'btn danger', text: 'Сбросить всё',
          onclick: () => modal({
            title: 'Сбросить настройки?',
            width: 420,
            body: el('div', { class: 'confirm-text', text: 'Фильтры, закладки и рисунки будут удалены.' }),
            actions: [
              { label: 'Отмена', onClick: (c) => c() },
              {
                label: 'Сбросить', primary: true,
                onClick: async (c) => {
                  await fetch('/api/settings/reset', { method: 'POST' });
                  localStorage.clear();
                  c();
                  location.reload();
                },
              },
            ],
          }),
        }),
      ]),
    ]));
  }

  #export() {
    const blob = new Blob([JSON.stringify(settings.all(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = el('a', { href: url, download: `scalpscreener-settings-${Date.now()}.json` });
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast('Настройки выгружены', 'success');
  }

  async #import(file) {
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      await fetch('/api/settings/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      toast('Настройки загружены — перезагружаю', 'success');
      setTimeout(() => location.reload(), 800);
    } catch (err) {
      console.warn('[import]', err);
      toast('Файл не подошёл', 'error');
    }
  }
}
